import React from "react";
import { Alert, ScrollView, StyleSheet } from "react-native";
import * as MailComposer from "expo-mail-composer";
import { useRouter } from "expo-router";
import { SafeAreaView } from "react-native-safe-area-context";

import UserProfileHeader from "./Components/UserProfileHeader";

import SettingsSection from "@/screens/Settings/Components/SettingsGroup";
import type { SettingGroup } from "@/schemas/screen/settings";
import type { AppUserSession } from "@/schemas/auth/session";
import { isPublicVariant } from "@/utils/expo";
import { isAdminUser } from "@/utils/user";

const HORIZONTAL_PADDING = 12;

type Props = {
  session: AppUserSession;
};

function SettingsDrawer({ session }: Props): React.JSX.Element {
  const router = useRouter();

  const navigateToOpenEmail = (): void => {
    MailComposer.composeAsync({
      recipients: ["support@getkaiapp.com"],
      subject: `Report a Problem`,
      body: `Please provide a brief description of what you are reporting.`,
    }).catch(() => {
      Alert.alert(
        "Email Client Not Available",
        "Please contact support@getkaiapp.com",
        [{ text: "OK" }],
      );
    });
  };

  // Hide the diagnostics screen for non-admins on the public variants.
  const showDiagnosticsScreen =
    isPublicVariant() && !isAdminUser(session.identity.username) ? false : true;

  const appSettings: SettingGroup[] = [
    {
      id: 1,
      name: "Account Management",
      icon: "shield",
      items: [
        {
          id: 1,
          name: "My Plan",
          emoji: "🏆",
          navigateTo: (): void => {
            router.push("/(main)/account/subscription");
          },
        },
        {
          id: 2,
          name: "Update Password",
          emoji: "🔑",
          navigateTo: (): void => {
            router.push("/(main)/account/update-password");
          },
        },
        {
          id: 3,
          name: "Block Users",
          emoji: "🚫",
          navigateTo: (): void => {
            router.push("/(main)/account/block-users");
          },
        },
        {
          id: 4,
          name: "My Account",
          emoji: "📝",
          navigateTo: (): void => {
            router.push("/(main)/account/delete-account");
          },
        },
        {
          id: 5,
          name: "Logout",
          emoji: "👋",
          navigateTo: (): void => {
            router.push("/(main)/account/logout");
          },
        },
      ],
    },
    {
      id: 2,
      name: "Troubleshooting",
      icon: "wrench",
      items: [
        {
          id: 4,
          name: "App Updates",
          emoji: "🔄",
          navigateTo: (): void => {
            router.push("/(main)/account/app-update");
          },
        },
        {
          id: 5,
          name: "Cache Management",
          emoji: "🗑️",
          navigateTo: (): void => {
            router.push("/(main)/account/cache-management");
          },
        },
        {
          id: 6,
          name: "Push Notification",
          emoji: "🔔",
          navigateTo: (): void => {
            router.push("/(main)/account/push-notifications");
          },
        },
        ...(showDiagnosticsScreen
          ? [
              {
                id: 7,
                name: "Diagnostics",
                emoji: "🧹",
                navigateTo: (): void => {
                  router.push("/(main)/account/diagnostics");
                },
              },
            ]
          : []),
      ],
    },
    {
      id: 3,
      name: "Support",
      icon: "question",
      items: [
        {
          id: 8,
          name: "Privacy Policy",
          emoji: "🔒",
          link: "https://getkaiapp.com/privacy",
        },
        {
          id: 9,
          name: "Terms & Privacy",
          emoji: "ℹ️",
          link: "https://getkaiapp.com/terms-of-service",
        },
        {
          id: 10,
          name: "Report a Problem",
          emoji: "🚨",
          navigateTo: () => navigateToOpenEmail(),
        },
      ],
    },
  ];

  return (
    <SafeAreaView edges={["top"]} style={styles.drawerContent}>
      <ScrollView>
        {session && <UserProfileHeader session={session} />}
        {appSettings.map((group) => (
          <SettingsSection key={group.id} group={group} />
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  drawerContent: {
    flex: 1,
    paddingLeft: HORIZONTAL_PADDING,
  },
});

export default SettingsDrawer;
